<template>
  <v-row v-if="!fullScreenExpanded" class="filter-bar mb-3 flex-grow-0">
    <v-col :cols="hasHighlightProductEnabled ? 4 : 6" class="py-2 px-0">
      <div class="d-flex assortment-filter">
        <span class="assortment-text mx-2">{{ tkey('assortment') }}:</span>
        <v-select
          :model-value="selectedCanvas"
          :items="canvases"
          :item-title="getAssortmentOptionText"
          item-value="_id"
          hide-details
          white
          class="rtls-select rtls-select--white"
          :menu-props="{ 'z-index': 200 }"
          @update:model-value="changeCanvas"
        />
      </div>
    </v-col>
    <v-col :cols="hasHighlightProductEnabled ? 8 : 6" class="d-flex py-2 px-0 justify-end">
      <div class="d-flex assortment-filter justify-end">
        <div v-if="hasCanvasFilterLegendEnabled">
          <v-icon class="mr-2" @click="setShowFilterLegend(!showFilterLegend)">
            {{ 'mdi-information-outline' }}
          </v-icon>
        </div>
        <span v-if="hasHighlightProductEnabled" key="" class="assortment-text mx-2"
          >{{ tkey('highlight') }}:</span
        >
        <div v-if="hasHighlightProductEnabled">
          <ScenarioAssortmentsFilter mode="highlight" />
        </div>
        <span class="assortment-text mx-2">{{ tkey('filter') }}:</span>
        <div class="mr-2">
          <ScenarioAssortmentsFilter mode="filter" />
        </div>
        <AssortmentSearch
          id="canvasSearch"
          item-text="productKeyDisplay"
          :items="getFilteredProducts.products"
          :item-label="getRowLabel"
          :on-search="onSearch"
          :placeholder="tkey('exampleSearch')"
          :on-selection="onSelection"
          clear-on-selection
        />
      </div>
      <div class="d-flex align-start mr-2">
        <ScenarioAssortmentsControls :disabled="isEditingDisabled" />
      </div>
    </v-col>
  </v-row>
</template>

<script>
import { useInternationalization } from '@/composables/use-internationalization';
import { usePermission } from '@/composables/use-permission';
import { useAssortmentCanvasStore } from '@/stores/assortment-canvas';
import { useContextStore } from '@/stores/context';
import { get } from 'lodash';
import { mapActions, mapState } from 'pinia';
import { useDomUtility } from '@/composables/dom-utils';
import productUtils from '@/utils/product-utils';

export default {
  setup() {
    const { canEditAssortmentCanvas } = usePermission();
    const { tkey } = useInternationalization('assortmentCanvasPage.toolbar');
    const { scrollToProductInCanvas, addActiveSearchBorderToProductElement } = useDomUtility();

    return {
      canEditAssortmentCanvas,
      tkey,
      scrollToProductInCanvas,
      addActiveSearchBorderToProductElement,
    };
  },
  computed: {
    ...mapState(useAssortmentCanvasStore, [
      'fullScreenExpanded',
      'showFilterLegend',
      'selectedCanvas',
      'canvases',
      'getFilteredProducts',
    ]),
    ...mapState(useContextStore, ['getClientConfig']),

    isEditingDisabled() {
      return !this.canEditAssortmentCanvas;
    },

    hasHighlightProductEnabled() {
      return get(this.getClientConfig, 'features.highlightProductEnabled', false);
    },

    hasCanvasFilterLegendEnabled() {
      return get(this.getClientConfig, 'features.canvasFilterLegendEnabled', false);
    },
  },

  watch: {
    '$route.params.scenarioId': function () {
      this.changeCanvasRenderingStatus(true);
      this.fetchCanvases();
      this.closeSpacebreakSettingsPanel();
    },
  },

  methods: {
    ...mapActions(useAssortmentCanvasStore, [
      'fetchCanvases',
      'changeCanvasRenderingStatus',
      'closeSpacebreakSettingsPanel',
      'setCanvasRenderingStatus',
      'setShowFilterLegend',
      'setIsProductSidebarOpen',
      'setDashboardProduct',
    ]),

    changeCanvas(canvasId) {
      this.setIsProductSidebarOpen(false);
      this.setDashboardProduct(null);
      if (canvasId !== this.$route.params.canvasId) {
        this.changeCanvasRenderingStatus(true);
      }

      this.$router.push({
        name: this.$route.name,
        params: { ...this.$route.params, canvasId },
        query: this.$route.query,
      });
      this.closeSpacebreakSettingsPanel();
    },

    getAssortmentOptionText(option) {
      const canvas = this.canvases.find(c => c._id === option._id);

      return `${canvas.clusterName || this.tkey('noCluster')} - ${canvas.storeClassName || ''}`;
    },

    onSelection({ productKey }) {
      const productElement = this.scrollToProductInCanvas({ productKey });
      // then add an active class and then remove it.
      this.addActiveSearchBorderToProductElement(productElement);

      // find the popup component and click on it
      const popupElement = document.getElementById(`product-tile-popup-${productKey}`);
      const rect = popupElement.getBoundingClientRect();
      const event = new MouseEvent('click', {
        clientX: rect.left,
        clientY: rect.top,
      });
      popupElement.dispatchEvent(event);
    },

    getRowLabel(product) {
      return `${product.productKeyDisplay} (${product.itemDescription})`;
    },

    onSearch(product, searchValue) {
      const { productKeyDisplay, itemDescription } = product;

      return productUtils.searchProductByKeyOrDescription(
        searchValue,
        productKeyDisplay,
        itemDescription
      );
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.filter-bar {
  border-top: 1px solid $assortment-primary-colour;
  background-color: $assortment-filter-bar-bg-colour;

  .assortment-text {
    font-size: 1.4rem;
    font-weight: 600;
    color: $assortment-text-colour;
  }
  .assortment-filter {
    width: 100%;
    align-items: flex-start;
  }
}
</style>
